import { useEffect, useState } from "react";
import moment from "moment";
import {
  IconPrinter,
  IconMapPin,
  IconBriefcase,
  IconMail,
  IconWorld,
  IconSchool,
  IconHeart,
  IconBulb,
  IconListCheck,
  IconMessageChatbot,
  IconChartBar,
  IconCalendar,
} from "@tabler/icons-react";
import styles from "./profileReport.module.css";
import { talentProfile } from "../../../services/talentCalls";
import { currencyFormat, getPercent } from "../../../utils/functions";
import { mapToScoreComparisonData } from "../../ClientDashboard/TalentProfile/components/ScoreComparison";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";

const ratingFor = (score) => {
  if (score >= 85) return { label: "Excellent", cls: styles.excellent };
  if (score >= 70) return { label: "Good", cls: styles.good };
  return { label: "Fair", cls: styles.fair };
};

const ProfileReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await talentProfile();
        setData(res?.data?.data?.talent || {});
      } catch (_) {
        setData({});
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    if (data?.firstName || data?.lastName) {
      document.title = `${data?.firstName || ""} ${data?.lastName || ""}`.trim();
    }
  }, [data]);

  if (loading) return <FullScreenLoader />;

  const initials = `${data?.firstName?.[0] || ""}${data?.lastName?.[0] || ""}`.toUpperCase();
  const experiences = data?.workHistoryExperience || [];
  const earliestStart = experiences
    .map((e) => e?.startDate)
    .filter(Boolean)
    .sort((a, b) => new Date(a) - new Date(b))[0];
  const yearsExperience = earliestStart ? moment().diff(moment(earliestStart), "years") : null;

  const scores = mapToScoreComparisonData(data);

  return (
    <div className={styles.reportPage}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.printBtn} onClick={() => window.print()}>
          <IconPrinter size={16} /> Print / Save as PDF
        </button>
      </div>

      <div className={styles.page}>
        <div className={styles.grid}>
          {/* Identity */}
          <div>
            <div className={styles.avatar}>{initials}</div>
            <div className={styles.name}>
              {data?.firstName} {data?.lastName}
            </div>
            <div className={styles.role}>{data?.pryRole}</div>

            <div className={styles.metaList}>
              {data?.currentLocation && (
                <div className={styles.metaItem}>
                  <IconMapPin size={15} /> {data.currentLocation}
                </div>
              )}
              {yearsExperience !== null && (
                <div className={styles.metaItem}>
                  <IconBriefcase size={15} /> {yearsExperience} years experience
                </div>
              )}
              {data?.email && (
                <div className={styles.metaItem}>
                  <IconMail size={15} /> {data.email}
                </div>
              )}
            </div>

            <div className={styles.salary}>
              <div className={styles.salaryLabel}>Salary Expectation</div>
              <div className={styles.salaryValue}>
                &#8358;{currencyFormat(Number(data?.desiredCompensation?.lowestAmount ?? 0))}
                <span>&rarr;</span>
                &#8358;{currencyFormat(Number(data?.desiredCompensation?.highestAmount ?? 0))}
              </div>
            </div>

            <div className={styles.summaryBox}>
              <div className={styles.summaryLabel}>Summary</div>
              <p className={styles.summaryText}>{data?.summary}</p>

              {!!data?.workMode?.length && (
                <div className={styles.prefList}>
                  {data.workMode.map((m) => (
                    <div className={styles.prefItem} key={m}>
                      <IconWorld size={14} /> Open to {m}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main sections */}
          <div className={styles.main}>
            {!!experiences.length && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconBriefcase size={18} />
                    </div>
                    <div className={styles.cardTitle}>Work History</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  {experiences.map((item, i) => (
                    <div className={styles.expItem} key={i}>
                      <div className={styles.expRail}>
                        {item?.isCurrentJob ? (
                          <span className={`${styles.expDate} ${styles.expDateNow}`}>Current</span>
                        ) : (
                          <span className={styles.expDate}>
                            {item?.endDate ? moment(item.endDate).format("MMM yyyy") : ""}
                          </span>
                        )}
                        <span className={`${styles.expDot} ${!item?.isCurrentJob ? styles.expDotEnd : ""}`} />
                        {i < experiences.length - 1 && <span className={styles.expLine} />}
                      </div>
                      <div>
                        <div className={styles.expTitle}>
                          <IconBriefcase size={17} /> {item?.jobTitle}
                        </div>
                        <div className={styles.expCompany}>{item?.companyName}</div>
                        {item?.industry && (
                          <div className={styles.expIndustry}>
                            <b>Industry</b>
                            <span className={styles.sep}>|</span>
                            {item.industry}
                          </div>
                        )}
                        <span className={styles.expPeriod}>
                          <IconCalendar size={13} /> {moment(item?.startDate).format("MMM yyyy")} —{" "}
                          {item?.isCurrentJob ? "Present" : moment(item?.endDate).format("MMM yyyy")}
                        </span>
                        {item?.jobDescription && <div className={styles.expDesc}>{item.jobDescription}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!data?.educations?.length && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconSchool size={18} />
                    </div>
                    <div className={styles.cardTitle}>Education</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  {data.educations.map((item, i) => (
                    <div className={styles.eduItem} key={i}>
                      <div>
                        <div className={styles.eduSchoolName}>{item?.schoolName}</div>
                        <div className={styles.eduDegreeRow}>
                          <span className={styles.eduField}>{item?.fieldOfStudy}</span>
                          <span className={styles.eduSep}>&middot;</span>
                          <span className={styles.eduDegree}>{item?.degree}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!data?.specificPriorities?.length && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconHeart size={18} />
                    </div>
                    <div className={styles.cardTitle}>Work Priorities</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.priorityList}>
                    {data.specificPriorities.map((p) => (
                      <span className={styles.priorityChip} key={p}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!!(data?.primarySkills?.length || data?.secondarySkills?.length) && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconBulb size={18} />
                    </div>
                    <div className={styles.cardTitle}>Skills</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.skillList}>
                    {[...(data?.primarySkills || []), ...(data?.secondarySkills || [])].map((s) => (
                      <span className={styles.skillChip} key={s}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!!scores.length && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconListCheck size={18} />
                    </div>
                    <div className={styles.cardTitle}>Abilities</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.abilitiesGrid}>
                    {scores.map((s) => {
                      const r = ratingFor(s.talent);
                      return (
                        <div className={styles.detailRow} key={s.name}>
                          <span className={styles.detailLabel}>{s.name}</span>
                          <span className={`${styles.detailValue} ${r.cls}`}>{r.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {!!data?.psychometricTestResult?.summary && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconMessageChatbot size={18} />
                    </div>
                    <div className={styles.cardTitle}>AI Summary</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <p className={styles.aiText}>{data.psychometricTestResult.summary}</p>
                </div>
              </div>
            )}

            {data?.submittedPsychometricTest && !!scores.length && (
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <div className={styles.cardIcon}>
                      <IconChartBar size={18} />
                    </div>
                    <div className={styles.cardTitle}>Score Comparison</div>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.scoreLegend}>
                    <span className={styles.scoreLegendItem}>
                      <span className={`${styles.scoreLegendDot} ${styles.scoreLegendDotTalent}`} /> Talent Score
                    </span>
                    <span className={styles.scoreLegendItem}>
                      <span className={`${styles.scoreLegendDot} ${styles.scoreLegendDotAi}`} /> AI Score
                    </span>
                  </div>
                  {scores.map((s) => (
                    <div className={styles.scoreTraitRow} key={s.name}>
                      <div className={styles.scoreTraitName}>{s.name}</div>
                      <div className={styles.scoreBarsWrap}>
                        <div className={`${styles.scoreBarCol} ${styles.scoreBarColTalent}`}>
                          <div className={`${styles.scoreBarTrack} ${styles.scoreBarTrackTalent}`}>
                            <div className={styles.scoreBarFillTalent} style={{ width: `${s.talent}%` }} />
                          </div>
                          <span className={`${styles.scoreVal} ${styles.scoreValTalent}`}>{s.talent}</span>
                        </div>
                        <div className={`${styles.scoreBarCol} ${styles.scoreBarColAi}`}>
                          <div className={styles.scoreBarTrack}>
                            <div className={styles.scoreBarFillAi} style={{ width: `${s.ai}%` }} />
                          </div>
                          <span className={`${styles.scoreVal} ${styles.scoreValAi}`}>{s.ai}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <p>Confidential AI-assisted report generated by Awasource. For informational and career development purposes only. Results may vary.</p>
          <p>Created with AWASOURCE.COM</p>
        </div>
      </div>
    </div>
  );
};

export default ProfileReport;
